import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { createSlice } from '@reduxjs/toolkit';
import {
  EquipmentGroupDetailsType,
  EquipmentGroupListItemType,
  TestNotificationResponse,
} from '../../types/equipment-group/equipment-group.type';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import {
  deleteEquipmentGroupDetails,
  getEquipmentGroupDetails,
  getEquipmentGroupsList,
  getEquipmentGroupTypesList,
  getPublicEquipmentGroupsDetails,
  patchEquipmentGroupDetails,
  postEquipmentGroupDetails,
  postTestNotification,
} from './equipmentGroups.action';
import snackbarUtils from '../../utils/SnackbarUtils';
import { EquipmentGroupTypeListItemType } from '../../types/equipment-group/equipment-group-type/equipment-group-type.type';
import { LocationType } from '../../types/location/location.type';

type EGRouteParams = RouteParamsType & {
  status: string;
  in_service: string;
  country_id: string;
  country_category: string;
};

export interface EquipmentGroupsState {
  egsList: null | PaginationType<EquipmentGroupListItemType>;
  egsListFetchStatus: FETCH_STATUS;
  egTypesList: null | Array<EquipmentGroupTypeListItemType>;
  egTypesListFetchStatus: FETCH_STATUS;
  egsRouteParams: EGRouteParams;
  egDetails: null | EquipmentGroupDetailsType;
  egDetailsFetchStatus: FETCH_STATUS;
  egDetailsPostStatus: FETCH_STATUS;
  egDetailsPost: null | EquipmentGroupDetailsType;
  egDetailsPatchStatus: FETCH_STATUS;
  egDetailsDeleteStatus: FETCH_STATUS;
  publicEGDetails: null | (LocationType & { equipment_group: EquipmentGroupDetailsType });
  publicEGDetailsFetchStatus: FETCH_STATUS;
  testNotificationStatus: FETCH_STATUS;
  testNotificationResult: null | TestNotificationResponse;
}

export const initialEquipmentGroupsRouteParams = {
  page: 1,
  limit: 10,
  order: '-equipment_group.id',
  q: '',
  status: '',
  in_service: 'all',
  country_id: '',
  country_category: '',
};

const INIT_STATE: EquipmentGroupsState = {
  egsRouteParams: initialEquipmentGroupsRouteParams,
  egsList: null,
  egsListFetchStatus: FETCH_STATUS.NULL,
  egDetails: null,
  egDetailsFetchStatus: FETCH_STATUS.NULL,
  egDetailsPostStatus: FETCH_STATUS.NULL,
  egDetailsPost: null,
  egDetailsPatchStatus: FETCH_STATUS.NULL,
  egDetailsDeleteStatus: FETCH_STATUS.NULL,
  egTypesList: null,
  egTypesListFetchStatus: FETCH_STATUS.NULL,
  publicEGDetails: null,
  publicEGDetailsFetchStatus: FETCH_STATUS.NULL,
  testNotificationStatus: FETCH_STATUS.NULL,
  testNotificationResult: null,
};

export const equipmentGroupsSlice = createSlice({
  name: 'equipmentGroups',
  initialState: INIT_STATE,
  reducers: {
    setEGsList: (state, action) => {
      state.egsList = action.payload;
    },
    setEGsListFetchStatus: (state, action) => {
      state.egsListFetchStatus = action.payload;
    },
    setEGDetails: (state, action) => {
      state.egDetails = action.payload;
    },
    setEGDetailsFetchStatus: (state, action) => {
      state.egDetailsFetchStatus = action.payload;
    },
    setEGDetailsPostStatus: (state, action) => {
      state.egDetailsPostStatus = action.payload;
    },
    setEGDetailsPost: (state, action) => {
      state.egDetailsPost = action.payload;
    },
    setEGDetailsPatchStatus: (state, action) => {
      state.egDetailsPatchStatus = action.payload;
    },
    setEGDetailsDeleteStatus: (state, action) => {
      state.egDetailsDeleteStatus = action.payload;
    },
    setEGTypesList: (state, action) => {
      state.egTypesList = action.payload;
    },
    setEGTypesListFetchStatus: (state, action) => {
      state.egTypesListFetchStatus = action.payload;
    },
    setPublicEGsDetails: (state, action) => {
      state.publicEGDetails = action.payload;
    },
    setPublicEGsDetailsFetchStatus: (state, action) => {
      state.publicEGDetailsFetchStatus = action.payload;
    },
    setTestNotificationStatus: (state, action) => {
      state.testNotificationStatus = action.payload;
    },
    setTestNotificationResult: (state, action) => {
      state.testNotificationResult = action.payload;
    },
  },

  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: EGRouteParams }>;
      if (payload.reducer === 'equipmentGroups') {
        state.egsRouteParams = { ...state.egsRouteParams, ...payload.data };
      }
    });

    // CREATE BATTERIES DETAILS
    builder.addCase(postEquipmentGroupDetails.pending, state => {
      state.egDetailsPostStatus = FETCH_STATUS.PENDING;
      state.egDetailsPost = null;
    });
    builder.addCase(postEquipmentGroupDetails.rejected, (state, action) => {
      state.egDetailsPostStatus = FETCH_STATUS.REJECTED;
      state.egDetailsPost = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postEquipmentGroupDetails.fulfilled, (state, action) => {
      state.egDetailsPostStatus = FETCH_STATUS.FULFILLED;
      state.egDetailsPost = action.payload;
      setTimeout(() => {
        snackbarUtils.success('Equipment Group created successfully');
      });
    });

    // GET BATTERIES LIST
    builder.addCase(getEquipmentGroupsList.pending, state => {
      state.egsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getEquipmentGroupsList.rejected, (state, action) => {
      if (action.payload) {
        state.egsListFetchStatus = FETCH_STATUS.REJECTED;
        // state.egsList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getEquipmentGroupsList.fulfilled, (state, action) => {
      state.egsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.egsList = action.payload;
    });

    // GET BATTERIES TYPES LIST
    builder.addCase(getEquipmentGroupTypesList.pending, state => {
      state.egTypesListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getEquipmentGroupTypesList.rejected, (state, action) => {
      state.egTypesListFetchStatus = FETCH_STATUS.REJECTED;
      state.egTypesList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getEquipmentGroupTypesList.fulfilled, (state, action) => {
      state.egTypesListFetchStatus = FETCH_STATUS.FULFILLED;
      state.egTypesList = action.payload;
    });

    // GET BATTERIES DETAILS
    builder.addCase(getEquipmentGroupDetails.pending, state => {
      state.egDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getEquipmentGroupDetails.rejected, (state, action) => {
      state.egDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.egDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getEquipmentGroupDetails.fulfilled, (state, action) => {
      state.egDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.egDetails = action.payload;
    });

    // PATCH BATTERIES DETAILS
    builder.addCase(patchEquipmentGroupDetails.pending, state => {
      state.egDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchEquipmentGroupDetails.rejected, (state, action) => {
      state.egDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchEquipmentGroupDetails.fulfilled, state => {
      state.egDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Equipment Group updated successfully');
      });
    });

    // DELETE BATTERIES DETAILS
    builder.addCase(deleteEquipmentGroupDetails.pending, state => {
      state.egDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteEquipmentGroupDetails.rejected, (state, action) => {
      state.egDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteEquipmentGroupDetails.fulfilled, state => {
      state.egDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Equipment Group deleted successfully');
      });
    });

    // GET PUBLIC EQUIPMENT GROUP DETAILS
    builder.addCase(getPublicEquipmentGroupsDetails.pending, state => {
      state.publicEGDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getPublicEquipmentGroupsDetails.rejected, (state, action) => {
      state.publicEGDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.publicEGDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getPublicEquipmentGroupsDetails.fulfilled, (state, action) => {
      state.publicEGDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.publicEGDetails = action.payload;
    });

    // POST TEST NOTIFICATION
    builder.addCase(postTestNotification.pending, state => {
      state.testNotificationStatus = FETCH_STATUS.PENDING;
      state.testNotificationResult = null;
    });
    builder.addCase(postTestNotification.rejected, (state, action) => {
      state.testNotificationStatus = FETCH_STATUS.REJECTED;
      state.testNotificationResult = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postTestNotification.fulfilled, (state, action) => {
      state.testNotificationStatus = FETCH_STATUS.FULFILLED;
      state.testNotificationResult = action.payload;
      setTimeout(() => {
        snackbarUtils.success('Test notification sent successfully');
      });
    });
  },
});

export const {
  setEGDetailsDeleteStatus,
  setEGDetailsFetchStatus,
  setEGDetailsPatchStatus,
  setEGDetailsPostStatus,
  setEGDetailsPost,
  setEGsListFetchStatus,
  setEGDetails,
  setEGsList,
  setEGTypesListFetchStatus,
  setEGTypesList,
  setPublicEGsDetailsFetchStatus,
  setPublicEGsDetails,
  setTestNotificationStatus,
  setTestNotificationResult,
} = equipmentGroupsSlice.actions;

export default equipmentGroupsSlice.reducer;
